/**
 * Own-XP API (specs/xp-and-ranks). Server derives level/rank from the ledger;
 * the client re-derives progress locally only for display parity checks.
 */

import { API_ENDPOINTS } from '../../../constants/api';

type ApiClient = (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>;

export interface XpSummary {
  totalXp: number;
  /** Derived level 1..100 (XP-based). */
  level: number;
  /** Derived rank 1..10 — coin-based (specs/coin-based-ranks); mirrors `peakCoins`. */
  rank: number;
  /** All-time-high coin balance the rank is derived from. */
  peakCoins: number;
  xpIntoLevel: number;
  xpForLevel: number;
  xpToday: number;
}

type XpEnvelope = { success?: boolean; data?: Partial<XpSummary>; error?: string };

function toSummary(body: XpEnvelope): XpSummary {
  const d = body.data ?? {};
  return {
    totalXp: d.totalXp ?? 0,
    level: d.level ?? 1,
    rank: d.rank ?? 1,
    peakCoins: d.peakCoins ?? 0,
    xpIntoLevel: d.xpIntoLevel ?? 0,
    xpForLevel: d.xpForLevel ?? 0,
    xpToday: d.xpToday ?? 0,
  };
}

export async function fetchXpSummary(apiClient: ApiClient): Promise<XpSummary> {
  const response = await apiClient(API_ENDPOINTS.users.xp);
  const body = response.data as XpEnvelope;
  if (response.status >= 400 || !body.success) {
    throw new Error(body.error ?? 'Failed to load XP');
  }
  return toSummary(body);
}

export type XpClientMilestone = 'onboarding_complete' | 'avatar_customized';

/** Report a whitelisted one-time milestone; returns the fresh summary. */
export async function reportXpMilestone(
  apiClient: ApiClient,
  kind: XpClientMilestone,
): Promise<XpSummary> {
  const response = await apiClient(API_ENDPOINTS.users.xpMilestones, {
    method: 'POST',
    data: { kind },
  });
  const body = response.data as XpEnvelope;
  if (response.status >= 400 || !body.success) {
    throw new Error(body.error ?? 'Failed to record milestone');
  }
  return toSummary(body);
}
